import type React from "react";
import { useCallback, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import logo from "@/assets/logo-crealavoro.png";
import { MobileMenu } from "./MobileMenu";

export const Navigation: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const handleCloseMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false);
  }, []);

  const navLinks = [
    { path: "/", label: "Home" },
    { path: "/studenti", label: "Per Studenti" },
    { path: "/laureati", label: "Per Laureati" },
    { path: "/enti", label: "Enti e Associazioni" },
    { path: "/opportunita", label: "Opportunità" },
    { path: "/risorse", label: "Risorse" },
    { path: "/contatti", label: "Contatti" },
  ];

  return (
    <>
      {/* Skip Navigation Link for Accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-[var(--color-primary)] focus:text-white focus:rounded-lg focus:shadow-lg"
      >
        Salta al contenuto principale
      </a>
      <header className="sticky top-0 z-[var(--z-sticky)] bg-[var(--color-surface)]/80 backdrop-blur-md border-b border-[var(--color-border)] shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center transition-transform duration-200 hover:scale-105"
              aria-label="Crea Lavoro - Home"
            >
              <img src={logo} alt="Crea Lavoro" className="h-12 w-auto" />
            </Link>

            {/* Navigation Links - Desktop */}
            <div className="hidden md:flex items-center space-x-1 lg:space-x-2">
              {navLinks.map(({ path, label }) => (
                <Link
                  key={path}
                  to={path}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-all duration-[var(--transition-base)] ${
                    isActive(path)
                      ? "text-primary bg-[var(--color-primary)]/10"
                      : "text-text-secondary hover:text-primary hover:bg-[var(--color-surface-elevated)]"
                  }`}
                  aria-current={isActive(path) ? "page" : undefined}
                >
                  {label}
                </Link>
              ))}
            </div>

            {/* Mobile Menu Button */}
            <button
              type="button"
              onClick={() => setIsMobileMenuOpen(true)}
              className="md:hidden p-2 rounded-md text-text-secondary hover:text-primary hover:bg-[var(--color-surface-elevated)] transition-all duration-[var(--transition-fast)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:ring-offset-2 focus:ring-offset-[var(--color-surface)]"
              aria-label="Apri menu di navigazione"
              aria-expanded={isMobileMenuOpen}
            >
              <svg
                className="h-6 w-6"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      <MobileMenu isOpen={isMobileMenuOpen} onClose={handleCloseMobileMenu} navLinks={navLinks} />
    </>
  );
};

export default Navigation;
